import { BILLING_CHART_MODE } from '../vocabulary.js';
import { escapeHtml } from '../escape-html.js';
import { formatEuro, sumMoney } from './billing.core.js';
import { ONE_OFF_PART_NAME } from './report.core.js';

/**
 * Data shaping and SVG markup for the billing overview's charts. Pure: no DOM access. Every chart is drawn at
 * the pixel width it is given, so its text keeps its size on a phone.
 */

/**
 * Series colors, CSS custom properties defined on the admin's `:root`. One meaning per color on every chart:
 * what comes in, what is kept, what goes out. The three were validated as a categorical set on the white
 * surface; the aqua sits below 3:1 contrast, so every figure the graph shows is also in the results table.
 */
export const SERIES_COLORS = Object.freeze({
  revenue: 'var(--chart-revenue)',
  profit: 'var(--chart-profit)',
  cost: 'var(--chart-cost)',
});

const BAR_MAX = 24;
const GAP = 2;
const RADIUS = 4;
const TICK_COUNT = 4;
const FONT = 11;
const COLUMN_HEIGHT = 220;

/** Hatched fill of a column's one-off part, defined once per chart that has one. */
const ONE_OFF_PATTERN = 'one-off-hatch';

/** The legend swatch of the one-off part, a CSS background matching `ONE_OFF_PATTERN`. */
const ONE_OFF_SWATCH = 'repeating-linear-gradient(135deg, var(--chart-revenue) 0 2px, '
  + 'color-mix(in srgb, var(--chart-revenue) 35%, transparent) 2px 5px)';

/** @typedef {{label: string, color: string}} Series */
/** @typedef {{title: string, rows: Array<[string, number]>}} Tip */
/**
 * @typedef {object} Category
 * @property {string} label
 * @property {number[]} values one per series
 * @property {Tip} tip what the tooltip shows for this category
 * @property {number} [key] year of an interactive report column
 * @property {boolean} [selected] highlights the selected report year
 * @property {string[]} [colors] optional per-category series colors (for losses)
 * @property {number[]} [parts] per series, the part of a positive value drawn hatched at its data end
 */

/**
 * Clean axis ticks covering `min`..`max` and zero, about four steps of 1, 2, 2.5 or 5 times a power of ten.
 *
 * @param {number} min
 * @param {number} max
 * @returns {number[]} ascending
 */
export function niceTicks(min, max) {
  const low = Math.min(0, min);
  const high = Math.max(0, max);
  const span = high - low || 1;
  const raw = span / TICK_COUNT;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw);
  const first = Math.floor(low / step);
  const last = Math.ceil(high / step) || 1;
  return Array.from({ length: last - first + 1 }, (_, i) => Number(((first + i) * step).toFixed(10)));
}

/**
 * @param {number} value
 * @returns {string} a compact euro figure for an axis tick, e.g. `€ 1.200` or `€ 2,5`
 */
function tickLabel(value) {
  return formatEuro(value).replace(/,00$/, '');
}

/**
 * @returns {string} the attributes that hand a category's tooltip to the component, which builds it with
 *   `textContent`
 */
function tipAttributes(tip) {
  return `data-tip-title="${escapeHtml(tip.title)}" data-tip-rows="${escapeHtml(JSON.stringify(tip.rows))}"`;
}

/**
 * A bar from `from` to `to` along its value axis, square at the baseline and rounded at the data end.
 *
 * @param {{x: number, y: number, width: number, height: number}} box
 * @param {'up'|'down'} end the side of the box that holds the data end
 * @returns {string} an SVG path; a box too small for the rounding is drawn square
 */
function barPath({ x, y, width, height }, end) {
  const r = Math.min(RADIUS, height / 2, width / 2);
  const f = (n) => Number(n.toFixed(2));
  if (end === 'up') {
    return `M${f(x)},${f(y + height)}V${f(y + r)}Q${f(x)},${f(y)} ${f(x + r)},${f(y)}H${f(x + width - r)}`
      + `Q${f(x + width)},${f(y)} ${f(x + width)},${f(y + r)}V${f(y + height)}Z`;
  }
  return `M${f(x)},${f(y)}V${f(y + height - r)}Q${f(x)},${f(y + height)} ${f(x + r)},${f(y + height)}`
    + `H${f(x + width - r)}Q${f(x + width)},${f(y + height)} ${f(x + width)},${f(y + height - r)}V${f(y)}Z`;
}

/**
 * Vertical columns, one group per category with a column per series, from a baseline at zero.
 *
 * @param {{series: Series[], categories: Category[], width: number, label: string}} chart
 * @returns {string} SVG markup
 */
export function columnChart({ series, categories, width, label }) {
  const values = categories.flatMap((category) => category.values);
  const ticks = niceTicks(Math.min(...values), Math.max(...values));
  const left = 64;
  const top = 8;
  const bottom = 24;
  const plotWidth = Math.max(1, width - left - 4);
  const plotHeight = COLUMN_HEIGHT - top - bottom;
  const low = ticks[0];
  const high = ticks[ticks.length - 1];
  const y = (value) => top + ((high - value) / (high - low)) * plotHeight;
  const band = plotWidth / categories.length;
  const barWidth = Math.max(2, Math.min(BAR_MAX, (band * 0.7 - GAP * (series.length - 1)) / series.length));
  const groupWidth = barWidth * series.length + GAP * (series.length - 1);
  // Label every column when they fit, else every second one.
  const labelEvery = band < 44 ? 2 : 1;

  const grid = ticks.map((tick) => `
      <line class="grid${tick === 0 ? ' baseline' : ''}" x1="${left}" x2="${left + plotWidth}" y1="${y(tick)}" y2="${y(tick)}"/>
      <text class="tick" x="${left - 6}" y="${y(tick) + FONT / 3}" text-anchor="end">${escapeHtml(tickLabel(tick))}</text>`).join('');
  const groups = categories.map((category, index) => {
    const x0 = left + band * index + (band - groupWidth) / 2;
    const bars = category.values.map((value, s) => {
      const height = Math.abs(y(value) - y(0));
      const x = x0 + s * (barWidth + GAP);
      const fill = category.colors?.[s] ?? series[s].color;
      const part = value > 0 ? Math.min(category.parts?.[s] ?? 0, value) : 0;
      if (part > 0) {
        const base = y(0) - y(value - part);
        return (base > 0 ? `<rect class="bar" fill="${fill}" x="${x}" y="${y(0) - base}" width="${barWidth}" height="${base}"/>` : '')
          + `<path class="bar one-off" fill="url(#${ONE_OFF_PATTERN})" d="${barPath({ x, y: y(value), width: barWidth, height: height - base }, 'up')}"/>`;
      }
      const box = { x, y: Math.min(y(value), y(0)), width: barWidth, height };
      return height === 0 ? '' : `<path class="bar" fill="${fill}" d="${barPath(box, value < 0 ? 'down' : 'up')}"/>`;
    }).join('');
    const axisLabel = index % labelEvery === 0
      ? `<text class="category" x="${left + band * index + band / 2}" y="${COLUMN_HEIGHT - 6}" text-anchor="middle">${escapeHtml(category.label)}</text>`
      : '';
    const interactive = category.key === undefined ? '' : `data-year="${category.key}" role="button" tabindex="0" aria-pressed="${!!category.selected}" aria-label="${escapeHtml('Select ' + category.label + ': ' + category.tip.rows.map(([name, value]) => name + ' ' + formatEuro(value)).join(', '))}"`;
    return `<g class="mark${category.selected ? ' selected' : ''}" ${interactive} ${tipAttributes(category.tip)}><title>${escapeHtml(category.tip.title + ': ' + category.tip.rows.map(([name, value]) => name + ' ' + formatEuro(value)).join(', '))}</title>
        <rect class="hit" x="${left + band * index}" y="${top}" width="${band}" height="${plotHeight}"/>${bars}${axisLabel}</g>`;
  }).join('');
  const defs = categories.some((category) => category.parts?.some((part) => part > 0))
    ? `<defs><pattern id="${ONE_OFF_PATTERN}" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
        <rect width="5" height="5" fill="var(--chart-revenue)" opacity="0.35"/><rect width="2" height="5" fill="var(--chart-revenue)"/></pattern></defs>`
    : '';
  return `<svg class="chart" width="${width}" height="${COLUMN_HEIGHT}" viewBox="0 0 ${width} ${COLUMN_HEIGHT}" role="${categories.some((category) => category.key !== undefined) ? 'group' : 'img'}" aria-label="${escapeHtml(label)}">${defs}${grid}${groups}</svg>`;
}

/**
 * @param {Series[]} series
 * @returns {string} a legend with a swatch per series, or nothing for a single series (the title names it)
 */
export function legend(series) {
  if (series.length < 2) return '';
  return `<ul class="legend">${series.map((entry) =>
    `<li><span class="swatch" style="background:${entry.color}"></span>${escapeHtml(entry.label)}</li>`).join('')}</ul>`;
}

/**
 * The one report graph: selectable years and the same invoice-based values as the detail table. A year with
 * one-off income says how much of its client income is one-off, and in Income and costs draws that part of
 * its Client income column hatched.
 *
 * @param {Map<number, number>} [oneOffByYear] one-off income per year, see `oneOffIncomePerYear`
 */
export function reportChart(years, mode, selectedYear, width, oneOffByYear = new Map()) {
  const net = mode === BILLING_CHART_MODE.NET;
  const series = net ? [{label: 'Profit / loss', color: SERIES_COLORS.profit}] : [
    {label: 'Client income', color: SERIES_COLORS.revenue}, {label: 'Costs', color: SERIES_COLORS.cost},
  ];
  const oneOffLegend = !net && years.some(({year}) => oneOffByYear.get(year) > 0)
    ? [{label: 'One-off (not recurring)', color: ONE_OFF_SWATCH}] : [];
  const categories = years.map(({year, totals}) => {
    const costs = sumMoney([totals.revenue, -totals.profit, totals.platformCosts]);
    const oneOff = oneOffByYear.has(year) ? [[ONE_OFF_PART_NAME, oneOffByYear.get(year)]] : [];
    return {
      key: year, selected: year === selectedYear, label: String(year),
      values: net ? [totals.netProfit] : [totals.revenue, costs],
      colors: net ? [totals.netProfit < 0 ? 'var(--color-danger)' : SERIES_COLORS.profit] : undefined,
      parts: net ? undefined : [oneOffByYear.get(year) ?? 0, 0],
      tip: {title: String(year), rows: [['Client income', totals.revenue], ...oneOff, ['Costs', costs], ['Result', totals.netProfit]]},
    };
  });
  return legend([...series, ...oneOffLegend]) + columnChart({series, categories, width, label: net ? 'Profit or loss by year. Select a year for details.' : 'Income and costs by year. Select a year for details.'});
}
